import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default images for each page/section (fallbacks)
const DEFAULT_IMAGES = {
  home: {
    hero: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1920&q=80',
    founder: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=1200&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=500&q=80',
      'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=500&q=80',
      'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=500&q=80',
      'https://images.unsplash.com/photo-1511578314322-379afb476865?w=500&q=80',
      'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=500&q=80',
      'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=500&q=80',
      'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=500&q=80',
      'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=500&q=80',
    ],
    ventures: []
  },
  evoke: {
    hero: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1920&q=80',
    'service-1': null,
    'service-2': null,
    'service-3': null,
    gallery: [
      'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=500&q=80',
      'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=500&q=80',
      'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=500&q=80',
      'https://images.unsplash.com/photo-1511578314322-379afb476865?w=500&q=80',
      'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=500&q=80',
      'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=500&q=80',
    ]
  },
  studio: {
    hero: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=1920&q=80',
    featured: []
  },
  blog: {
    hero: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1920&q=80',
    featured: null
  },
  contact: {
    hero: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80',
    team: []
  },
  footer: {
    gallery: [
      'https://images.unsplash.com/photo-1769812343628-5a2d12f297f7?w=400&q=80',
      'https://images.unsplash.com/photo-1633029790372-3960e5fab043?w=400&q=80',
      'https://images.unsplash.com/photo-1769812343323-5e06f29e0070?w=400&q=80',
      'https://images.unsplash.com/photo-1560113406-36a33855c51e?w=400&q=80',
    ]
  }
};

/**
 * Hook to fetch images for a specific page
 * @param {string} page - Page name (home, evoke, studio, blog, contact, footer)
 * @returns {object} - { images, loading, getImage, getImages }
 */
export const usePageImages = (page) => {
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImages();
  }, [page]);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/images?page=${page}`);
      if (response.ok) {
        const data = await response.json();
        // Organize by section
        const organized = {};
        data.forEach(img => {
          const section = img.section || 'general';
          if (!organized[section]) {
            organized[section] = [];
          }
          organized[section].push(img);
        });
        // Sort each section by display_order
        Object.keys(organized).forEach(section => {
          organized[section].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        });
        setImages(organized);
      }
    } catch (error) {
      console.error(`Error fetching images for ${page}:`, error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get single image URL for a section (first image or fallback)
   */
  const getImage = (section) => {
    const sectionImages = images[section];
    if (sectionImages && sectionImages.length > 0) {
      return sectionImages[0].url;
    }
    // Return default
    return DEFAULT_IMAGES[page]?.[section] || null;
  };

  /**
   * Get single image with position for a section
   */
  const getImageWithPosition = (section) => {
    const sectionImages = images[section];
    if (sectionImages && sectionImages.length > 0) {
      const img = sectionImages[0];
      return {
        url: img.url,
        position: `${img.focal_x ?? 50}% ${img.focal_y ?? 50}%`,
        alt: img.alt_text || img.title
      };
    }
    // Return default
    return {
      url: DEFAULT_IMAGES[page]?.[section] || null,
      position: '50% 50%',
      alt: 'Image'
    };
  };

  /**
   * Get all images for a section (array of URLs)
   */
  const getImages = (section) => {
    const sectionImages = images[section];
    if (sectionImages && sectionImages.length > 0) {
      return sectionImages.map(img => img.url);
    }
    // Return defaults
    const defaults = DEFAULT_IMAGES[page]?.[section];
    return Array.isArray(defaults) ? defaults : [];
  };

  /**
   * Get full image objects for a section (with title, alt, position, etc.)
   */
  const getImageObjects = (section) => {
    const sectionImages = images[section];
    if (sectionImages && sectionImages.length > 0) {
      return sectionImages.map(img => ({
        ...img,
        position: `${img.focal_x ?? 50}% ${img.focal_y ?? 50}%`
      }));
    }
    // Create default objects
    const defaults = DEFAULT_IMAGES[page]?.[section];
    if (Array.isArray(defaults)) {
      return defaults.map((url, idx) => ({
        id: `default-${idx}`,
        url,
        title: `Image ${idx + 1}`,
        alt_text: `Image ${idx + 1}`,
        position: '50% 50%'
      }));
    }
    return [];
  };

  return {
    images,
    loading,
    getImage,
    getImageWithPosition,
    getImages,
    getImageObjects
  };
};

/**
 * Hook to fetch ALL page images at once
 */
export const useAllPageImages = () => {
  const [allImages, setAllImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllImages();
  }, []);

  const fetchAllImages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/images/pages`);
      if (response.ok) {
        const data = await response.json();
        setAllImages(data);
      }
    } catch (error) {
      console.error('Error fetching all page images:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImage = (page, section) => {
    const sectionImages = allImages[page]?.[section];
    if (sectionImages && sectionImages.length > 0) {
      return sectionImages[0].url;
    }
    return DEFAULT_IMAGES[page]?.[section] || null;
  };

  const getImages = (page, section) => {
    const sectionImages = allImages[page]?.[section];
    if (sectionImages && sectionImages.length > 0) {
      return sectionImages.map(img => img.url);
    }
    const defaults = DEFAULT_IMAGES[page]?.[section];
    return Array.isArray(defaults) ? defaults : [];
  };

  return {
    allImages,
    loading,
    getImage,
    getImages
  };
};

export default usePageImages;
